import { Fragment, useEffect, useState } from "react";
import { LogIn, RefreshCw, Trash2, Users, X, KeyRound, Copy, UserPlus } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { apiError } from "../../lib/api";
import { copyToClipboard } from "../../lib/clipboard";
import { useAuth } from "../../context/AuthContext";

export default function SuperSchools() {
  const [schools, setSchools] = useState([]);
  const [plans, setPlans] = useState([]);
  const [expanded, setExpanded] = useState(null);
  const [pupils, setPupils] = useState([]);
  const [pupilConfirm, setPupilConfirm] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [resetTarget, setResetTarget] = useState(null);
  const [resetLink, setResetLink] = useState("");
  const [tempPw, setTempPw] = useState("");
  const [showAdd, setShowAdd] = useState(false);
  const [addForm, setAddForm] = useState({ school_name: "", email: "", password: "", year_system: "scottish", plan_id: "", activate: false, fees_waived: false });
  const { saveSession } = useAuth();
  const navigate = useNavigate();

  const load = () => {
    api.get("/admin/schools").then((r) => setSchools(r.data)).catch((e) => toast.error(apiError(e)));
    api.get("/admin/plans").then((r) => setPlans(r.data.filter((p) => p.audience === "school" && p.active))).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const update = async (id, patch, msg) => {
    try {
      await api.put(`/admin/schools/${id}`, patch);
      toast.success(msg);
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const addSchool = async () => {
    if (addForm.password.length < 8) return toast.error("Password needs at least 8 characters");
    try {
      await api.post("/admin/schools", { ...addForm, plan_id: addForm.plan_id || null });
      toast.success(`${addForm.school_name} created`);
      setShowAdd(false);
      setAddForm({ school_name: "", email: "", password: "", year_system: "scottish", plan_id: "", activate: false, fees_waived: false });
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const impersonate = async (s) => {
    try {
      const { data } = await api.post(`/admin/schools/${s.id}/impersonate`);
      saveSession(data);
      toast.success(`Now viewing as ${s.name}`);
      window.location.href = "/admin";
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const togglePupils = async (s) => {
    if (expanded === s.id) { setExpanded(null); return; }
    try {
      const { data } = await api.get(`/admin/schools/${s.id}/pupils`);
      setPupils(data);
      setExpanded(s.id);
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const removePupil = async (p) => {
    if (pupilConfirm !== p.id) { setPupilConfirm(p.id); return; }
    try {
      await api.delete(`/admin/pupils/${p.id}`);
      toast.success(`${p.name} removed`);
      setPupilConfirm(null);
      setPupils(pupils.filter((x) => x.id !== p.id));
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const removeSchool = async () => {
    if (!confirmDelete) { setConfirmDelete(true); return; }
    try {
      await api.delete(`/admin/schools/${deleteTarget.id}`);
      toast.success(`${deleteTarget.name} deleted`);
      setDeleteTarget(null);
      setConfirmDelete(false);
      setExpanded(null);
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const doResetLink = async () => {
    try {
      const { data } = await api.post(`/admin/schools/${resetTarget.id}/reset-password`, {});
      setResetLink(`${window.location.origin}${data.reset_path}`);
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const doTempPw = async () => {
    if (tempPw.length < 8) return toast.error("At least 8 characters");
    try {
      await api.post(`/admin/schools/${resetTarget.id}/reset-password`, { temporary_password: tempPw });
      toast.success(`Temporary password set for ${resetTarget.name}`);
      setResetTarget(null);
      setTempPw("");
      setResetLink("");
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  return (
    <div data-testid="super-schools-page">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <span className="nb-label">Schools</span>
          <h1 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight mt-2">School accounts</h1>
          <p className="text-zinc-500 mt-2 max-w-xl">Assign plans, waive fees, manage pupil accounts, or log in as a school to help with onboarding.</p>
        </div>
        <div className="flex gap-2">
          <button onClick={() => setShowAdd(true)} data-testid="school-add-btn" className="nb-btn bg-[#A7E4D3] px-4 py-2 text-sm"><UserPlus className="w-4 h-4" /> Add school</button>
          <button onClick={load} data-testid="schools-refresh-btn" className="nb-btn bg-white px-4 py-2 text-sm"><RefreshCw className="w-4 h-4" /> Refresh</button>
        </div>
      </div>

      <div className="mt-8 nb-card overflow-x-auto">
        <table className="w-full border-collapse min-w-[860px]" data-testid="schools-table">
          <thead>
            <tr className="bg-zinc-900 text-white text-left">
              <th className="p-3 text-xs font-bold tracking-wider uppercase">School</th>
              <th className="p-3 text-xs font-bold tracking-wider uppercase">Pupils</th>
              <th className="p-3 text-xs font-bold tracking-wider uppercase">Plan</th>
              <th className="p-3 text-xs font-bold tracking-wider uppercase">Status</th>
              <th className="p-3 text-xs font-bold tracking-wider uppercase">Fees waived</th>
              <th className="p-3 text-xs font-bold tracking-wider uppercase">Actions</th>
            </tr>
          </thead>
          <tbody>
            {schools.map((s) => (
              <Fragment key={s.id}>
                <tr className="border-t-2 border-zinc-900" data-testid={`school-row-${s.id}`}>
                  <td className="p-3">
                    <div className="font-bold">{s.name}</div>
                    <div className="text-xs text-zinc-500">{s.email} · code {s.join_code}</div>
                  </td>
                  <td className="p-3 font-bold" data-testid={`school-pupils-${s.id}`}>{s.pupils_count}</td>
                  <td className="p-3">
                    <select className="nb-input h-9 py-1 text-sm" data-testid={`school-plan-select-${s.id}`} value={s.plan_id || ""}
                      onChange={(e) => update(s.id, { plan_id: e.target.value }, "Plan updated")}>
                      <option value="">No plan</option>
                      {plans.map((p) => <option key={p.id} value={p.id}>{`${p.name} (£${p.price_gbp}/yr, ≤${p.max_pupils})`}</option>)}
                    </select>
                  </td>
                  <td className="p-3">
                    <span className={`px-2 py-0.5 rounded-lg border-2 border-zinc-900 text-xs font-bold ${
                      s.fees_waived ? "bg-[#D8B4FE]" : s.subscription_status === "active" ? "bg-[#A7E4D3]" : s.subscription_status === "trialing" ? "bg-[#FDE68A]" : "bg-red-200"}`}
                      data-testid={`school-status-${s.id}`}>
                      {s.fees_waived ? "waived" : s.subscription_status || "—"}
                    </span>
                  </td>
                  <td className="p-3">
                    <button data-testid={`school-waive-toggle-${s.id}`}
                      onClick={() => update(s.id, { fees_waived: !s.fees_waived }, s.fees_waived ? "Fees re-enabled" : "Fees waived")}
                      className={`px-3 py-1.5 rounded-lg border-2 border-zinc-900 text-xs font-bold transition-colors duration-150 ${s.fees_waived ? "bg-[#D8B4FE]" : "bg-white hover:bg-zinc-100"}`}>
                      {s.fees_waived ? "Waived" : "Waive"}
                    </button>
                  </td>
                  <td className="p-3">
                    <div className="flex gap-2 flex-wrap">
                      <button onClick={() => impersonate(s)} data-testid={`school-impersonate-${s.id}`}
                        className="nb-btn bg-[#1E3A8A] text-white px-3 py-1.5 text-xs">
                        <LogIn className="w-3.5 h-3.5" /> Log in as
                      </button>
                      <button onClick={() => togglePupils(s)} data-testid={`school-pupils-toggle-${s.id}`}
                        className="nb-btn bg-white px-3 py-1.5 text-xs">
                        <Users className="w-3.5 h-3.5" /> {expanded === s.id ? "Hide" : "Pupils"}
                      </button>
                      <button onClick={() => { setResetTarget(s); setResetLink(""); setTempPw(""); }} data-testid={`school-reset-${s.id}`}
                        className="nb-btn bg-[#7DD3FC] px-3 py-1.5 text-xs">
                        <KeyRound className="w-3.5 h-3.5" /> Reset pw
                      </button>
                      <button onClick={() => { setDeleteTarget(s); setConfirmDelete(false); }} data-testid={`school-delete-${s.id}`}
                        className="nb-btn bg-red-100 text-red-700 px-3 py-1.5 text-xs">
                        <Trash2 className="w-3.5 h-3.5" /> Delete
                      </button>
                    </div>
                  </td>
                </tr>
                {expanded === s.id && (
                  <tr key={`${s.id}-pupils`} className="border-t-2 border-zinc-900 bg-[#FDFBF7]" data-testid={`school-pupils-panel-${s.id}`}>
                    <td colSpan={6} className="p-4">
                      {pupils.length === 0 ? (
                        <p className="text-sm text-zinc-500 font-semibold" data-testid={`school-pupils-empty-${s.id}`}>No pupil accounts.</p>
                      ) : (
                        <div className="space-y-2">
                          {pupils.map((p) => (
                            <div key={p.id} className="flex items-center justify-between gap-3 rounded-xl border-2 border-zinc-900 bg-white p-2.5" data-testid={`pupil-row-${p.id}`}>
                              <div>
                                <span className="font-bold text-sm">{p.name}</span>
                                <span className="text-xs text-zinc-500 font-semibold ml-2">{p.year_group}{p.sub_group ? ` · ${p.sub_group}` : ""}</span>
                              </div>
                              <button onClick={() => removePupil(p)} data-testid={`pupil-delete-${p.id}`}
                                className="nb-btn bg-red-100 text-red-700 px-3 py-1 text-xs">
                                <Trash2 className="w-3.5 h-3.5" /> {pupilConfirm === p.id ? "Click again to confirm" : "Delete"}
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                    </td>
                  </tr>
                )}
              </Fragment>
            ))}
            {schools.length === 0 && <tr><td colSpan={6} className="p-8 text-center text-zinc-500 text-sm border-t-2 border-zinc-900" data-testid="schools-empty">No schools yet.</td></tr>}
          </tbody>
        </table>
      </div>

      {resetTarget && (
        <div className="fixed inset-0 z-50 bg-zinc-900/50 flex items-center justify-center p-4" data-testid="school-reset-modal">
          <div className="nb-card p-6 w-full max-w-md bg-white">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-display font-bold text-xl">Reset password — {resetTarget.name}</h3>
              <button onClick={() => { setResetTarget(null); setResetLink(""); setTempPw(""); }} data-testid="school-reset-close"
                className="p-1.5 rounded-lg border-2 border-zinc-900 bg-white hover:bg-zinc-100"><X className="w-4 h-4" /></button>
            </div>
            <p className="text-sm text-zinc-600">Generate a one-hour reset link to send to <span className="font-bold">{resetTarget.email}</span>, or set a temporary password directly.</p>
            {!resetLink ? (
              <button onClick={doResetLink} data-testid="school-reset-link-btn" className="nb-btn w-full bg-[#7DD3FC] py-2.5 mt-4">
                <KeyRound className="w-4 h-4" /> Generate reset link
              </button>
            ) : (
              <div className="mt-4">
                <input readOnly className="nb-input text-xs" data-testid="school-reset-link-out" value={resetLink} />
                <button onClick={async () => { const ok = await copyToClipboard(resetLink); ok ? toast.success("Link copied") : toast.error("Couldn't copy"); }} data-testid="school-reset-copy-btn"
                  className="nb-btn w-full bg-[#A7E4D3] py-2 mt-2 text-sm"><Copy className="w-4 h-4" /> Copy link</button>
              </div>
            )}
            <div className="mt-4 pt-4 border-t-2 border-zinc-100">
              <label className="nb-label" htmlFor="school-temp-pw">Or set a temporary password</label>
              <div className="flex gap-2 mt-2">
                <input id="school-temp-pw" className="nb-input flex-1" data-testid="school-reset-temp-input" placeholder="Min 8 characters"
                  value={tempPw} onChange={(e) => setTempPw(e.target.value)} />
                <button onClick={doTempPw} data-testid="school-reset-temp-btn" className="nb-btn bg-[#FDE68A] px-4 py-2 text-sm shrink-0">Set</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showAdd && (
        <div className="fixed inset-0 z-50 bg-zinc-900/50 flex items-center justify-center p-4" data-testid="school-add-modal">
          <div className="nb-card p-6 w-full max-w-md bg-white max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-display font-bold text-xl">Add a school</h3>
              <button onClick={() => setShowAdd(false)} data-testid="school-add-close"
                className="p-1.5 rounded-lg border-2 border-zinc-900 bg-white hover:bg-zinc-100"><X className="w-4 h-4" /></button>
            </div>
            <div className="space-y-3">
              <input className="nb-input" placeholder="School name" data-testid="school-add-name"
                value={addForm.school_name} onChange={(e) => setAddForm({ ...addForm, school_name: e.target.value })} />
              <input className="nb-input" type="email" placeholder="Admin email" data-testid="school-add-email"
                value={addForm.email} onChange={(e) => setAddForm({ ...addForm, email: e.target.value })} />
              <input className="nb-input" placeholder="Password (min 8 chars)" data-testid="school-add-password"
                value={addForm.password} onChange={(e) => setAddForm({ ...addForm, password: e.target.value })} />
              <div className="grid grid-cols-2 gap-2">
                <select className="nb-input" data-testid="school-add-year-system" value={addForm.year_system}
                  onChange={(e) => setAddForm({ ...addForm, year_system: e.target.value })}>
                  <option value="scottish">Scottish years</option>
                  <option value="english">English years</option>
                  <option value="college">College years</option>
                </select>
                <select className="nb-input" data-testid="school-add-plan" value={addForm.plan_id}
                  onChange={(e) => setAddForm({ ...addForm, plan_id: e.target.value })}>
                  <option value="">No plan</option>
                  {plans.map((p) => <option key={p.id} value={p.id}>{`${p.name} (£${p.price_gbp}/yr)`}</option>)}
                </select>
              </div>
              <label className="flex items-center gap-2 text-sm font-bold">
                <input type="checkbox" data-testid="school-add-activate" checked={addForm.activate}
                  onChange={(e) => setAddForm({ ...addForm, activate: e.target.checked })} />
                Activate subscription immediately
              </label>
              <label className="flex items-center gap-2 text-sm font-bold">
                <input type="checkbox" data-testid="school-add-waived" checked={addForm.fees_waived}
                  onChange={(e) => setAddForm({ ...addForm, fees_waived: e.target.checked })} />
                Waive fees (comp)
              </label>
              <button onClick={addSchool} data-testid="school-add-submit" className="nb-btn w-full bg-[#A7E4D3] py-2.5">
                <UserPlus className="w-4 h-4" /> Create school
              </button>
            </div>
          </div>
        </div>
      )}

      {deleteTarget && (
        <div className="fixed inset-0 z-50 bg-zinc-900/50 flex items-center justify-center p-4" data-testid="school-delete-modal">
          <div className="nb-card p-6 w-full max-w-md bg-white">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-display font-bold text-xl text-red-700">Delete {deleteTarget.name}?</h3>
              <button onClick={() => { setDeleteTarget(null); setConfirmDelete(false); }} data-testid="school-delete-cancel"
                className="p-1.5 rounded-lg border-2 border-zinc-900 bg-white hover:bg-zinc-100"><X className="w-4 h-4" /></button>
            </div>
            <p className="text-sm text-zinc-600">
              Removes the school and every pupil, teacher, timetable, notice, homework and exam record belonging to it. Any active Stripe subscription is cancelled. This can't be undone.
            </p>
            <button onClick={removeSchool} data-testid="school-delete-confirm" className="nb-btn w-full bg-red-600 text-white py-2.5 mt-5">
              <Trash2 className="w-4 h-4" /> {confirmDelete ? "Click again to confirm permanent deletion" : "Delete school"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
